import { useEffect, useReducer } from 'react';
import { useSocket } from '../../shared/contexts/SocketContext';
import AdminUsersPageView from './AdminUsersPageView';
import { fetchUsers, updateUserRoleApi, createUser, deleteUser, updateUserPaymentMode, fetchUserPaymentMode, type AdminUserItem, type PaymentModeValue } from './adminUsers.api';

interface AdminUsersState {
  loading: boolean;
  error: string | null;
  users: AdminUserItem[];
}

type AdminUsersAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_USERS'; payload: AdminUserItem[] }
  | { type: 'ADD_USER'; payload: AdminUserItem }
  | { type: 'UPDATE_USER_ROLE'; payload: { id: string; role: 'admin' | 'user' } }
  | { type: 'UPDATE_PAYMENT_MODE'; payload: { id: string; paymentMode: PaymentModeValue } }
  | { type: 'DELETE_USER'; payload: string }
  | { type: 'RESET_STATE' };

function adminUsersReducer(state: AdminUsersState, action: AdminUsersAction): AdminUsersState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_USERS':
      return { ...state, users: action.payload, error: null };
    case 'ADD_USER':
      return { ...state, users: [action.payload, ...state.users], error: null };
    case 'UPDATE_USER_ROLE':
      return {
        ...state,
        users: state.users.map(u => (u.id === action.payload.id ? { ...u, role: action.payload.role } : u)),
        error: null
      };
    case 'UPDATE_PAYMENT_MODE':
      return {
        ...state,
        users: state.users.map(u => (u.id === action.payload.id ? { ...u, paymentMode: action.payload.paymentMode } : u)),
        error: null
      };
    case 'DELETE_USER':
      return {
        ...state,
        users: state.users.filter(u => u.id !== action.payload),
        error: null
      };
    case 'RESET_STATE':
      return { loading: true, error: null, users: [] };
    default:
      return state;
  }
}

function AdminUsersPage() {
  const [state, dispatch] = useReducer(adminUsersReducer, {
    loading: true,
    error: null,
    users: []
  });

  const { addNotification } = useSocket();
  const envKey = import.meta.env.VITE_QreditCharge_API_KEY as string | undefined;
  const lsKey = typeof window !== 'undefined' ? window.localStorage?.getItem('QreditCharge_API_KEY') || undefined : undefined;
  const apiKeyMissing = !envKey && !lsKey;

  async function load() {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const list = await fetchUsers();
      dispatch({ type: 'SET_USERS', payload: list });
      const modes = await Promise.all(list.map(async (u) => {
        try { return { id: u.id, mode: await fetchUserPaymentMode(u.id) }; }
        catch { return { id: u.id, mode: null }; }
      }));
      const enriched = list.map(u => ({ ...u, paymentMode: modes.find(m => m.id === u.id)?.mode ?? null }));
      dispatch({ type: 'SET_USERS', payload: enriched });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to load users' });
      dispatch({ type: 'SET_USERS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }

  useEffect(() => { load(); }, []);

  async function onChangeRole(id: string, role: 'admin' | 'user') {
    try {
      await updateUserRoleApi(id, role);
      dispatch({ type: 'UPDATE_USER_ROLE', payload: { id, role } });
      addNotification({ type: 'success', message: 'User role updated' });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to update role' });
      addNotification({ type: 'error', message: e?.message || 'Failed to update role' });
    }
  }

  async function onCreate(email: string, password: string) {
    try {
      const res = await createUser(email, password);
      const id = String(res?.toCreate?._id || res?.view?.id || '');
      const newItem: AdminUserItem = {
        id,
        email: res?.view?.email || email,
        role: 'user',
        createdAt: new Date().toISOString(),
        lastLogin: new Date(0).toISOString()
      };
      dispatch({ type: 'ADD_USER', payload: newItem });
      addNotification({ type: 'success', message: 'User created' });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to create user' });
      addNotification({ type: 'error', message: e?.message || 'Failed to create user' });
    }
  }

  async function onChangePaymentMode(id: string, paymentMode: PaymentModeValue) {
    try {
      await updateUserPaymentMode(id, paymentMode);
      dispatch({ type: 'UPDATE_PAYMENT_MODE', payload: { id, paymentMode } });
      addNotification({ type: 'success', message: `Payment mode set to ${paymentMode}` });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to update payment mode' });
      addNotification({ type: 'error', message: e?.message || 'Failed to update payment mode' });
    }
  }

  async function onDelete(id: string) {
    try {
      await deleteUser(id);
      dispatch({ type: 'DELETE_USER', payload: id });
      addNotification({ type: 'success', message: 'User deleted' });
    } catch (e: any) {
      dispatch({ type: 'SET_ERROR', payload: e?.message || 'Failed to delete user' });
      addNotification({ type: 'error', message: e?.message || 'Failed to delete user' });
    }
  }

  return (
    <AdminUsersPageView 
      loading={state.loading} 
      error={state.error} 
      users={state.users} 
      onChangeRole={onChangeRole} 
      onChangePaymentMode={onChangePaymentMode}
      onCreate={onCreate} 
      onDelete={onDelete} 
      apiKeyMissing={apiKeyMissing} 
    />
  );
}

export default AdminUsersPage;
